import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { Offer } from '../../../model/offer';
import { ThemeService } from '../../../_services/theme.service';
import { AmenityDTO } from '../../../model/amentyDTO';

@Component({
  selector: 'ui-new-offer-icon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-new-offer-icon.component.html',
  styleUrl: './ui-new-offer-icon.component.scss',
})
export class UiNewOfferIconComponent implements AfterViewInit, OnDestroy {
  @Input() offer!: Offer | null;
  @Input() viewMode: 'tiles' | 'list' = 'tiles';

  // NOTE: iconRow is under an *ngIf, so it must be non-static.
  @ViewChild('iconRow') iconRowEl?: ElementRef<HTMLElement>;
  @ViewChild('tagRow') tagRowEl?: ElementRef<HTMLElement>;
  @ViewChildren('iconChip') iconChips!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('tagChip') tagChips!: QueryList<ElementRef<HTMLElement>>;

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private iconChipsChangesSub?: { unsubscribe?: () => void };
  private tagChipsChangesSub?: { unsubscribe?: () => void };
  private documentClickUnlisten?: (() => void) | null = null;
  private relayoutTimer?: number | null = null;
  private contentToggledUnlisten?: (() => void) | null = null;

  constructor(
    public themeService: ThemeService,
    private renderer: Renderer2,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngAfterViewInit(): void {
    this.scheduleRelayout();

    this.ensureResizeObserver();

    this.iconChipsChangesSub = this.iconChips?.changes.subscribe(() => {
      this.ensureResizeObserver();
      this.scheduleRelayout();
    });
    this.tagChipsChangesSub = this.tagChips?.changes.subscribe(() => {
      this.ensureResizeObserver();
      this.scheduleRelayout();
    });

    const offerCard = this.hostRef.nativeElement.closest('.offer-card');
    if (offerCard) {
      this.mutationObserver = new MutationObserver(() => this.scheduleRelayout());
      this.mutationObserver.observe(offerCard, { attributes: true, attributeFilter: ['class'] });
    }

    // Also listen for document clicks to catch outside clicks that change expanded state
    this.documentClickUnlisten = this.renderer.listen('document', 'click', () => {
      // Schedule a short debounce so class toggles from clicks settle
      this.scheduleRelayout();
    });

    // Listen for explicit content toggle events so we can relayout immediately
    this.contentToggledUnlisten = this.renderer.listen('window', 'ui:new:contentToggled', () => {
      // Give one animation frame for class changes and then relayout
      requestAnimationFrame(() => this.relayoutChips());
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.iconChipsChangesSub?.unsubscribe?.();
    this.tagChipsChangesSub?.unsubscribe?.();
    if (this.documentClickUnlisten) {
      this.documentClickUnlisten();
      this.documentClickUnlisten = null;
    }
    if (this.contentToggledUnlisten) {
      this.contentToggledUnlisten();
      this.contentToggledUnlisten = null;
    }
    if (this.relayoutTimer) {
      clearTimeout(this.relayoutTimer as any);
      this.relayoutTimer = null;
    }
  }

  iconSrc(svgName: string): string {
    return `assets/svg/${svgName}${this.themeService.isDarkMode() ? '' : '-black'}.svg`;
  }

  private relayoutChips(): void {
    // List view: always show everything
    if (this.viewMode === 'list') {
      this.showAllChips();
      return;
    }

    const offerCard = this.hostRef.nativeElement.closest('.offer-card') as HTMLElement | null;
    // Rely on the content-expanded flag exclusively (the card's 'expanded' class
    // is controlled by sharedService.currentIndex and may remain true even when
    // the content is collapsed). Use content-expanded to reflect content visibility.
    const isExpanded = !!offerCard && offerCard.classList.contains('content-expanded');
    if (isExpanded) {
      this.showAllChips();
      return;
    }

    this.relayoutRow(this.iconRowEl?.nativeElement, this.iconChips);
    this.relayoutRow(this.tagRowEl?.nativeElement, this.tagChips);
  }

  private relayoutRow(
    container: HTMLElement | undefined,
    chips: QueryList<ElementRef<HTMLElement>> | undefined,
  ): void {
    if (!container || !chips || chips.length === 0) return;

    this.renderer.addClass(container, 'measuring');
    try {
      const containerWidth = container.clientWidth;
      if (containerWidth <= 0) return;

      const containerStyle = getComputedStyle(container);
      const gap = this.parsePx(containerStyle.columnGap || containerStyle.gap);

      let used = 0;
      const chipsArr = chips.toArray();
      for (let i = 0; i < chipsArr.length; i++) {
        const el = chipsArr[i].nativeElement;
        const rectW = el.getBoundingClientRect().width;
        const elStyle = getComputedStyle(el);
        const marginLeft = this.parsePx(elStyle.marginLeft);
        const marginRight = this.parsePx(elStyle.marginRight);
        const chipWidth = rectW + marginLeft + marginRight;

        const projected = i === 0 ? chipWidth : used + gap + chipWidth;
        if (projected <= containerWidth) {
          this.renderer.removeClass(el, 'chip-hidden');
          used = projected;
        } else {
          this.renderer.addClass(el, 'chip-hidden');
        }
      }
    } finally {
      this.renderer.removeClass(container, 'measuring');
    }
  }

  private scheduleRelayout(): void {
    // Debounce relayout slightly so synchronous class toggles (from clicks)
    // have time to settle and avoid transient show-all behavior.
    if (this.relayoutTimer) {
      clearTimeout(this.relayoutTimer as any);
    }
    this.relayoutTimer = window.setTimeout(() => {
      this.relayoutTimer = null;
      this.relayoutChips();
    }, 40) as unknown as number;
  }

  private ensureResizeObserver(): void {
    const iconContainer = this.iconRowEl?.nativeElement;
    const tagContainer = this.tagRowEl?.nativeElement;
    if (!iconContainer && !tagContainer) return;
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.relayoutChips());
    }
    if (iconContainer) this.resizeObserver.observe(iconContainer);
    if (tagContainer) this.resizeObserver.observe(tagContainer);
  }

  private showAllChips(): void {
    this.iconChips?.forEach(c => this.renderer.removeClass(c.nativeElement, 'chip-hidden'));
    this.tagChips?.forEach(c => this.renderer.removeClass(c.nativeElement, 'chip-hidden'));
  }

  private parsePx(value: string | null | undefined): number {
    if (!value) return 0;
    // gap can be like "8px 10px"; prefer the last value (column gap)
    const parts = value.trim().split(/\s+/);
    const last = parts[parts.length - 1] ?? '0px';
    const n = parseFloat(last);
    return Number.isFinite(n) ? n : 0;
  }

  activeIcons(amenities: AmenityDTO): any {      
    return amenities?.icons?.filter(ic => !!ic?.flg_active) ?? [];
  }

}
